import { safeJsonParse } from "@swiggy-private/js-utils";
import type { AnalyticsEventPayload } from "@swiggy-private/analytics/dist/interfaces";
import type { IPLWidgetControllerCards } from "@swiggy-private/dls-react-mxn/dist/WidgetController/PLWidgetController/type";
import type { ILocalCart } from "@swiggy-private/portal-dineout-context";
import { safeStringify } from "@swiggy-private/portal-dineout-context";
import { CROSS_SELL_WIDGET_IMPRESSION } from "./constants";
import {
    buildParentCrossSellAnalyticsContext,
    getCrossSellParentImpressionSupplement,
} from "./crossSellAnalyticsHelpers";
import { findCategoryMetaForMenuItem } from "./crossSellMenuItemCategoryHelpers";
import type { CrossSellAnalyticsCustomizationRow, CrossSellNormalItem } from "./types";
import { isCurrentUserHost } from "../../../utils";
import { type CrossSellAnalyticsParams, MenuItemCategoryMeta, UserCategory } from "../../types";

type CrossSellWidgetImpressionContextRow = Record<
    string,
    string | number | boolean | string[] | CrossSellAnalyticsCustomizationRow[]
>;

export const buildCrossSellWidgetImpressionEventPayload = (args: {
    crossSellAnalytics: CrossSellAnalyticsParams;
    dishMap: Record<string, CrossSellNormalItem>;
    displayItems: CrossSellNormalItem[];
    localCart: ILocalCart | undefined;
    menuInitialData: IPLWidgetControllerCards[];
    pinStatus: string;
}): Partial<AnalyticsEventPayload> => {
    const parentItemId = args.crossSellAnalytics.sourceItemId;
    const parentDish = args.dishMap[parentItemId];
    const analytics = parentDish?.analytics;
    const parentItemInCart = args.localCart?.cartItems?.find(
        (line) => line.menu_item_id === parentItemId
    );
    const shownCrossSellItemIds = args.displayItems.map((d) => String(d.id ?? "")).filter(Boolean);
    const categoryMeta: MenuItemCategoryMeta | null = findCategoryMetaForMenuItem(
        args.menuInitialData,
        parentItemId
    );
    const userCategory = isCurrentUserHost(args.pinStatus)
        ? UserCategory.PRIMARY
        : UserCategory.SECONDARY;

    const parentContext = buildParentCrossSellAnalyticsContext({
        sourceItemId: args.crossSellAnalytics.sourceItemId,
        filterEnabled: args.crossSellAnalytics.filterEnabled,
        vegFilterEnabled: args.crossSellAnalytics.vegFilterEnabled,
        parentDish,
        parentItemInCart,
        categoryMeta,
        userCategory,
    });

    let contextStr: string;
    if (analytics?.context) {
        const withCartFields = analytics.context
            .replaceAll("{customizable}", String(parentContext.customizable))
            .replaceAll("{quantity}", String(parentContext.quantity))
            .replaceAll("{final_price}", String(parentContext.final_price));
        const parsed = safeJsonParse(withCartFields, {}) as CrossSellWidgetImpressionContextRow;
        const supplement = getCrossSellParentImpressionSupplement(parentContext);
        const merged: CrossSellWidgetImpressionContextRow = {
            ...parsed,
            ...supplement,
            shown_item_ids: shownCrossSellItemIds,
        };
        contextStr = safeStringify(merged, "-");
    } else {
        const merged = { ...parentContext, shown_item_ids: shownCrossSellItemIds };
        contextStr = safeStringify(merged, "-");
    }

    return {
        category: analytics?.screenName,
        action: CROSS_SELL_WIDGET_IMPRESSION.ACTION,
        label: CROSS_SELL_WIDGET_IMPRESSION.LABEL,
        value: CROSS_SELL_WIDGET_IMPRESSION.OBJECT_POSITION,
        source: CROSS_SELL_WIDGET_IMPRESSION.SOURCE,
        context: contextStr,
    };
};
